/* Light/dark theme mechanism for the Terminal.
 *
 * The shell root (and portal-rendered surfaces like SheetContent) carry
 * data-terminal-theme="dark" | "light", which flips the value tables in
 * terminal-theme.css — scoped to the terminal, never the document root.
 * Persists the choice to a cookie so SSR and first paint render the chosen
 * theme. Dark is the home theme (no OS preference).
 *
 * Tiny external store (no dependency) consumed via useSyncExternalStore. */
import { createIsomorphicFn } from "@tanstack/react-start"
import { getCookie } from "@tanstack/react-start/server"
import { useSyncExternalStore } from "react"

export type TerminalTheme = "dark" | "light"

const COOKIE_NAME = "wattbot-terminal-theme"
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const hasLightCookie = createIsomorphicFn()
  .server(() => getCookie(COOKIE_NAME) === "light")
  .client(() => document.cookie.split("; ").includes(`${COOKIE_NAME}=light`))

function readTheme(): TerminalTheme {
  return hasLightCookie() ? "light" : "dark"
}

// The client store. The server has no per-request module state, so it reads
// the request cookie on every render instead (getServerSnapshot).
let currentTheme: TerminalTheme =
  typeof window === "undefined" ? "dark" : readTheme()
const listeners = new Set<() => void>()

function setTerminalTheme(theme: TerminalTheme): void {
  if (theme === currentTheme) return
  currentTheme = theme
  document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): TerminalTheme {
  return currentTheme
}

// Hydration reads the same cookie the server rendered from, so it matches.
function getServerSnapshot(): TerminalTheme {
  return typeof window === "undefined" ? readTheme() : currentTheme
}

export function useTerminalTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return { theme, setTerminalTheme }
}
